import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import {
  RIBBON_SOCIAL,
  RIBBON_SOCIAL_DURATION_MS,
  RIBBON_SOCIAL_EASING,
  RIBBON_SOCIAL_PULSE,
  RIBBON_SOCIAL_PULSE_DURATION_MS,
  RIBBON_SOCIAL_PULSE_PASSES,
  ribbonSocialKeyframes,
  ribbonSocialPulseDelayMs,
  ribbonSocialPulseKeyframes,
  RIBBON_SOCIAL_STEP_MS,
} from '@/components/atoms/ribbonMotion';
import { company } from '@/config/company';

import { activeSocials, socialIcons } from './icons';

type Props = {
  /**
   * Glyph size. 16 in the ribbon's control cluster, where it sits beside a
   * 16px dismiss control and must not out-weigh it; 18 on the row of its own
   * that the ribbon gives it below md, where the only thing it shares the
   * line with is the other four icons.
   */
  iconSize?: number;
  /**
   * Padding around the glyph, in theme spacing units (4px each).
   *
   * Its own prop rather than something derived from `iconSize`, because the
   * two are set for unrelated reasons: the glyph is sized to balance what it
   * sits beside, and the padding is the tap target. The ribbon's control
   * cluster can defend 0.75 — 28px total — on the grounds that it only
   * appears from md up, where a pointer is the norm. The row below md has no
   * such defence and asks for more.
   */
  pad?: number;
  /**
   * Run the finite idle pulse once the row has settled. On by default — the
   * whole reason the row animates at all — and off for any caller that wants
   * the entrance alone. See `ribbonMotion.ts` for why it is three passes and
   * not a loop.
   */
  pulse?: boolean;
};

/**
 * The social links, restyled for the ribbon and animated as they arrive.
 *
 * A second component rather than a variant of `SocialIconLinks`, which is the
 * footer's: that one is a bordered button on a paper surface with a
 * `text.secondary` glyph, and every one of those decisions is wrong on a
 * brand-red bar. Sharing them through props would mean a component whose
 * every visual property is overridden by one of its two callers.
 *
 * **Motion sits on a wrapper inside the button, not on the button.** Three
 * things want to move these icons — the entrance, the idle pulse, and the
 * hover lift — and the first two are animations while the third is a
 * transition. An animation beats a transition on the same property outright,
 * so with all three on one element the hover lift was dead for as long as
 * either animation ran, and `animation-fill-mode: both` on the entrance also
 * held its `opacity: 1` keyframe forever, overriding the resting dim in `sx`
 * and silently disabling the hover brighten with it. Splitting them fixes
 * both: the wrapper owns `transform` for the two animations, the button owns
 * opacity, background and its own hover `transform`, and nothing overrides
 * anything.
 *
 * Per-icon delays go in `style` rather than `sx` so the stagger does not
 * generate one emotion class per icon — the same reason `WaveText` does it
 * for forty characters.
 */
export function RibbonSocialLinks({ iconSize = 16, pad = 0.75, pulse = true }: Props) {
  const count = activeSocials.length;
  if (count === 0) return null;

  // Both animations are declared on one element, so every longhand is a
  // matching pair. The pulse's `none` fill is load-bearing; see ribbonMotion.
  const animationName = pulse ? `${RIBBON_SOCIAL}, ${RIBBON_SOCIAL_PULSE}` : RIBBON_SOCIAL;
  const animationDuration = pulse
    ? `${RIBBON_SOCIAL_DURATION_MS}ms, ${RIBBON_SOCIAL_PULSE_DURATION_MS}ms`
    : `${RIBBON_SOCIAL_DURATION_MS}ms`;
  const animationTimingFunction = pulse
    ? `${RIBBON_SOCIAL_EASING}, ease-in-out`
    : RIBBON_SOCIAL_EASING;
  const animationIterationCount = pulse ? `1, ${RIBBON_SOCIAL_PULSE_PASSES}` : '1';
  const animationFillMode = pulse ? 'both, none' : 'both';

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{
        alignItems: 'center',
        [`@keyframes ${RIBBON_SOCIAL}`]: ribbonSocialKeyframes,
        [`@keyframes ${RIBBON_SOCIAL_PULSE}`]: ribbonSocialPulseKeyframes,
      }}
    >
      {activeSocials.map((s, index) => {
        const Icon = socialIcons[s.platform];
        const delays = pulse
          ? `${index * RIBBON_SOCIAL_STEP_MS}ms, ${ribbonSocialPulseDelayMs(index, count)}ms`
          : `${index * RIBBON_SOCIAL_STEP_MS}ms`;

        return (
          <IconButton
            key={s.platform}
            href={s.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${company.brandName} on ${s.label}`}
            size="small"
            // Under the theme's 44px touch target, for the same reason the
            // dismiss control beside it steps down: the row is 40px tall, and
            // these are a convenience rather than the bar's purpose. How far
            // under is the caller's call — see `pad`.
            sx={{
              minWidth: 0,
              minHeight: 0,
              p: pad,
              color: 'inherit',
              opacity: 0.85,
              transition: (t) =>
                t.transitions.create(['opacity', 'transform', 'background-color'], {
                  duration: t.transitions.duration.shorter,
                }),
              '&:hover, &:focus-visible': {
                opacity: 1,
                transform: 'translate3d(0, -2px, 0)',
                bgcolor: 'rgba(255,255,255,0.18)',
              },
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
                '&:hover, &:focus-visible': { transform: 'none' },
              },
            }}
          >
            <Box
              component="span"
              style={{ animationDelay: delays }}
              sx={{
                display: 'inline-flex',
                animationName,
                animationDuration,
                animationTimingFunction,
                animationIterationCount,
                animationFillMode,
                // No `will-change`: five permanently promoted layers to save
                // three beats of transform is the trade ribbonMotion.ts
                // declines for the headline's forty, and it declines it here.
                '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
              }}
            >
              <Icon sx={{ fontSize: iconSize }} />
            </Box>
          </IconButton>
        );
      })}
    </Stack>
  );
}
